import { useState, useEffect, useRef } from "react";
import RolesToolbar from "./RolesToolbar";
import RolesTable from "./RolesTable";
import Pagination from "../components/Pagination";
import api from "../../../src/api_interceptor/api";
import Loader from "../../components/loader/Loader";
import GenericModal from "../components/GenericModal";
import { useNavigate } from "react-router-dom";

const Roles = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const hasFetched = useRef(false);
  const [searchRole, setSearchRole] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [pendingRoleId, setPendingRoleId] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageLength] = useState(10);
  const navigate = useNavigate();

  const [modalState, setModalState] = useState({
    open: false,
    title: "",
    description: "",
    type: "success", // success | error | warning
  });

  const handleToggleStatus = (id, currentStatusCode) => {
    setPendingRoleId(id);
    // Check if currently active (StatusCodeId can be 1, true, or "1")
    const isCurrentlyActive = currentStatusCode === 1 || currentStatusCode === true || currentStatusCode === "1";
    // If currently active, we want to deactivate (set to false)
    // If currently inactive, we want to activate (set to true)
    const newStatusCode = !isCurrentlyActive; // Toggle: active -> false, inactive -> true
    setPendingStatus(newStatusCode);
    setModalOpen(true);
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    getRoleList();
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on search
      getRoleList();
    }, 400); // debounce

    return () => clearTimeout(delay);
  }, [searchRole]);

  useEffect(() => {
    if (hasFetched.current) {
      getRoleList();
    }
  }, [currentPage]);

  const showSuccess = (message) => {
    setModalState({
      open: true,
      title: "Success",
      description: message,
      type: "success",
    });
  };

  const showError = (message) => {
    setModalState({
      open: true,
      title: "Error",
      description: message || "Something went wrong. Please try again.",
      type: "error",
    });
  };

  const showWarning = (message) => {
    setModalState({
      open: true,
      title: "Action Not Allowed",
      description: message,
      type: "warning",
    });
  };

  const getRoleList = () => {
    setIsLoading(true);
    const API_URL = "api/GetAllRoles";
    const req_body = {
      filter: searchRole || "",
      pageIndex: currentPage - 1, // API expects 0-based index (will be incremented by 1 in backend)
      totalPage: totalPages,
      pageLength: pageLength,
      errorMessage: "",
      Type: "Role",
      SortAction: "None",
    };

    api
      .post(API_URL, req_body, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      })
      .then((response) => {
        setIsLoading(false);
        if (response && response.data) {
          // Check if AllRoleList exists first - if data exists, treat as success
          if (
            response.data.AllRoleList !== undefined &&
            response.data.AllRoleList !== null
          ) {
            setRoles(response.data.AllRoleList);
            setTotalPages(response.data.totalPage || 1);
          } else if (
            response.data.errorMessage &&
            response.data.errorMessage.toLowerCase() !== "success"
          ) {
            const errorMsg =
              response.data.errorMessage === "Exception"
                ? "An error occurred while loading role list. Please try again."
                : response.data.errorMessage || "Failed to load role list.";
            showError(errorMsg);
          } else {
            setRoles([]);
          }
        } else {
          showError("Invalid response from server. Please try again.");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Get role list error:", error);

        if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
          showError(
            "Unable to connect to server. Please check your internet connection."
          );
        } else if (error.response) {
          const errorMsg =
            error.response.data?.Message ||
            "Failed to load role list. Please try again.";
          showError(errorMsg);
        } else {
          showError("An unexpected error occurred. Please try again.");
        }
      });
  };

  const handleEditRole = (role) => {
    if (role.StatusCodeId !== 1 && role.StatusCodeId !== true) {
      showWarning("Please activate the role before editing.");
      return;
    }
    navigate(`/roles/edit/${role.ID}`);
  };

  const updateRoleStatus = (id, isActivated) => {
    setIsLoading(true);
    const API_URL = "api/UpdateRoleStatus";
    const req_body = {
      Id: id,
      isActivated: isActivated, // boolean: true for activate, false for deactivate
    };

    const actionText = isActivated ? "activated" : "deactivated";

    api
      .post(API_URL, req_body, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      })
      .then((response) => {
        setIsLoading(false);

        // API returns boolean directly
        if (response && response.data !== undefined && response.data !== null) {
          const returnedStatus = response.data;

          if (typeof returnedStatus === "boolean") {
            setModalOpen(false);
            showSuccess(`Role ${actionText} successfully.`);
            // List will refresh when user clicks OK on success modal
          } else {
            showError("Invalid response from server. Please try again.");
            setModalOpen(false);
          }
        } else {
          showError("Invalid response from server. Please try again.");
          setModalOpen(false);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setModalOpen(false);
        console.error("Update role status error:", error);

        if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
          showError(
            "Unable to connect to server. Please check your internet connection."
          );
        } else if (error.response) {
          if (error.response.status === 400) {
            showError("Failed to update role status. Please try again.");
          } else {
            const errorMsg =
              error.response.data?.Message ||
              `Failed to ${actionText} role. Please try again.`;
            showError(errorMsg);
          }
        } else {
          showError("An unexpected error occurred. Please try again.");
        }
      });
  };

  return (
    <>
      {isLoading && <Loader />}

      <div className="bg-white p-4 rounded-lg flex flex-col h-full min-h-0">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold leading-[1.3]">Roles</h1>
          <p className="text-base text-primary">Roles</p>
        </div>

        <RolesToolbar setSearchRole={setSearchRole} />

        <div className="flex-1 min-h-0">
          <RolesTable
            data={roles}
            onToggleStatus={handleToggleStatus}
            onEditRole={handleEditRole}
          />
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={roles.length}
          onPageChange={(newPage) => {
            setCurrentPage(newPage);
          }}
        />

        <GenericModal
          open={modalOpen}
          title="Confirm Action"
          description={`Are you sure you want to ${
            pendingStatus === true ? "activate" : "deactivate"
          } this role?`}
          primaryText="Yes"
          secondaryText="No"
          loading={isLoading}
          onPrimary={() => {
            updateRoleStatus(pendingRoleId, pendingStatus);
          }}
          onClose={() => {
            setModalOpen(false);
            setPendingRoleId(null);
            setPendingStatus(null);
          }}
        />

        <GenericModal
          open={modalState.open}
          title={modalState.title}
          description={modalState.description}
          type={modalState.type}
          primaryText="OK"
          onPrimary={() => {
            const wasSuccess = modalState.type === "success";
            setModalState((prev) => ({ ...prev, open: false }));
            if (wasSuccess) {
              getRoleList();
            }
          }}
          onClose={() => {
            const wasSuccess = modalState.type === "success";
            setModalState((prev) => ({ ...prev, open: false }));
            if (wasSuccess) {
              getRoleList();
            }
          }}
        />
      </div>
    </>
  );
};

export default Roles;
